import { gql, useMutation, useQuery } from '@apollo/client'
import { ForceExperimentInput } from '@jusbrasil-web/shared-apollo-client/__generated__/types'
import { useCallback, useContext, useEffect } from 'react'

import { TopbarContext } from '../context'
import {
  DeleteForcedExperimentMutationMutation,
  DeleteForcedExperimentMutationMutationVariables,
  ForceExperimentMutation,
  ForceExperimentMutationVariables,
  ForceExperimentQuery,
  ForceExperimentQueryVariables,
} from './__generated__/use-forced-experiment.graphql'

export const FORCED_EXPERIMENT_QUERY = gql`
  query ForceExperiment {
    root {
      forcedExperiment @ignoreExceptions {
        group
        experiment
        alternative
      }
    }
  }
`

export const FORCE_EXPERIMENT_MUTATION = gql`
  mutation ForceExperiment($input: ForceExperimentInput!) {
    forceExperiment(input: $input) {
      result
    }
  }
`

export const REMOVE_FORCED_EXPERIMENT_MUTATION = gql`
  mutation DeleteForcedExperimentMutation {
    deleteForcedExperiment(input: {}) {
      result
    }
  }
`

export function useForcedExperiment() {
  const { user } = useContext(TopbarContext)
  const { data: forcedExperimentData, loading: queryLoading } = useQuery<
    ForceExperimentQuery,
    ForceExperimentQueryVariables
  >(FORCED_EXPERIMENT_QUERY, {
    skip: !user?.admin,
  })

  const [
    forceExperimentMutation,
    { data: forceExperimentMutationResult, called: forceExperimentMutationCalled },
  ] = useMutation<ForceExperimentMutation, ForceExperimentMutationVariables>(
    FORCE_EXPERIMENT_MUTATION
  )

  const [
    deleteForcedExperimentMutation,
    {
      data: deleteForcedExperimentMutationResult,
      loading: deleteForcedExperimentMutationLoading,
      called: deleteForcedExperimentMutationCalled,
    },
  ] = useMutation<
    DeleteForcedExperimentMutationMutation,
    DeleteForcedExperimentMutationMutationVariables
  >(REMOVE_FORCED_EXPERIMENT_MUTATION)

  const hasForcedExperiment = !!forcedExperimentData?.root?.forcedExperiment

  const currentForcedExperimentGroup = forcedExperimentData?.root?.forcedExperiment?.group || ''
  const currentForcedExperimentName = forcedExperimentData?.root?.forcedExperiment?.experiment || ''
  const currentForcedExperimentVariation =
    forcedExperimentData?.root.forcedExperiment?.alternative || ''

  useEffect(() => {
    if (forceExperimentMutationCalled && forceExperimentMutationResult?.forceExperiment?.result) {
      // if new forced experiment is set, needs to wait 3s to sync with Argos
      // ref: https://github.com/jusbrasil/argos/blob/f6cbb07ea7bdae6a71746e81fb72745029fa0a5c/argos-api/src/main/scala/br/com/jusbrasil/argos/api/service/experiment/ForceExperiment.scala#L30
      const TIME_TO_SYNC_ARGOS = hasForcedExperiment ? 0 : 3000
      setTimeout(() => {
        window.location.reload()
      }, TIME_TO_SYNC_ARGOS)
    }
  }, [
    forceExperimentMutationCalled,
    forceExperimentMutationResult?.forceExperiment?.result,
    hasForcedExperiment,
  ])

  useEffect(() => {
    if (
      deleteForcedExperimentMutationCalled &&
      deleteForcedExperimentMutationResult?.deleteForcedExperiment?.result
    ) {
      window.location.reload()
    }
  }, [
    deleteForcedExperimentMutationCalled,
    deleteForcedExperimentMutationResult?.deleteForcedExperiment?.result,
  ])

  const setForcedExperiment = useCallback(
    (input: ForceExperimentInput) => {
      forceExperimentMutation({ variables: { input } })
    },
    [forceExperimentMutation]
  )

  return {
    // Data
    forcedExperimentData: forcedExperimentData?.root?.forcedExperiment,
    hasForcedExperiment,
    currentForcedExperimentGroup,
    currentForcedExperimentName,
    currentForcedExperimentVariation,

    // States
    queryLoading,
    forceExperimentMutationCalled,
    deleteForcedExperimentMutationLoading,

    // Actions
    setForcedExperiment,
    deleteForcedExperimentMutation: deleteForcedExperimentMutation,
  }
}
